import React, { useEffect, useMemo, useState } from 'react'
import { NavigationStackScreenComponent } from 'react-navigation-stack'
import { CustomHeader } from '../../components/CustomHeader'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import { atlTheme } from '../../atlTheme'
import {
  ATLButton,
  ATLCheckbox,
  ATLDateTimePicker,
  ATLDropdownSelector,
  ATLSwitchButton,
  ATLTextBody,
  ATLTextInput,
  ButtonTypeEnum,
  FontWeightEnum,
  KeyboardTypeEnum,
} from '@appsimples/atlantica-react-native'
import { icons } from '../../constants/searchAssets'
import { useGlobal } from '../../global/useGlobal'
import { shortMonth } from '../../constants/shortMonth'
import { openWhatsApp } from '../../utils/openWhatsApp'
import { BRIO_SAC_WHATSAPP_NUMBER } from '../../constants/whatsAppInfo'
import styled from 'styled-components/native'
import { getOnlyNumbers, ifMoneyInt } from '../../utils/formatters'
import dayjs from 'dayjs'
import { currencyMask } from '../../utils/masks'
import { getUserSaleContracts } from '../../services/main/getUserSaleContracts'
import { getStatement } from '../../services/main/getStatement'
import { platformConfig } from '../../constants/platform'

const ScreenContainer = styled.View`
  background-color: ${atlTheme.ColorSurface};
  border-bottom-right-radius: ${atlTheme.BorderRadiusCard}px;
  border-bottom-left-radius: ${atlTheme.BorderRadiusCard}px;
`
const ButtonContainer = styled.View`
  width: 40%;
  align-self: center;
`

export const AnticipateInstallmentScreen: NavigationStackScreenComponent = () => {
  const [lastInstallment, setLastInstallment] = useState(false)
  const [penultimateInstallment, setPenultimateInstallment] = useState(false)
  const [installmentsComing] = useGlobal('installmentsComing')
  const lastInstallmentInfo = installmentsComing[installmentsComing?.length - 1]
  const penultimateInstallmentInfo = installmentsComing[installmentsComing?.length - 2]
  const lastInstallmentDate = new Date(lastInstallmentInfo.dueDate)
  const penultimateInstallmentDate = new Date(penultimateInstallmentInfo.dueDate)
  const [payPartialValue, setPayPartialValue] = useState(false)
  const [dateValue, setDateValue] = useState<Date>()
  const [additionalValue, setAdditionalValue] = useState('')
  const [saleContractId, setSaleContractId] = useState<string>()
  const [userSaleContracts] = useGlobal('userSaleContracts')
  const [value, setValue] = useState<string>('')
  const [dropdownUserSaleContractsData] = useGlobal('dropdownUserSaleContractsData')
  const today = useMemo(() => new Date(), [])
  const [selectedUserSaleContract] = useGlobal('selectedUserSaleContract')

  useEffect(() => {
    getUserSaleContracts()
  }, [])

  if (value === '') {
    if (selectedUserSaleContract) {
      setValue(selectedUserSaleContract.text)
      setSaleContractId(userSaleContracts[selectedUserSaleContract.id].id)
    } else {
      setValue(`${userSaleContracts[0]?.construction?.name} - ${userSaleContracts[0].constructionUnit?.name}` || '')
      setSaleContractId(userSaleContracts[0].id)
    }
  }

  useEffect(() => {
    saleContractId && getStatement({ saleContractId: userSaleContracts[saleContractId]?.id })
  }, [saleContractId])

  const onPressSAC = () => {
    let whatsAppText = formatMessage(messages.financeFutureScreen_whatsApp_message)
    const title = installment => ifMoneyInt(installment.originalValue)
    const totalValue = installment => (payPartialValue ? additionalValue : ifMoneyInt(installment.originalValue))
    const originalDate = installmentDate =>
      shortMonth[installmentDate.getMonth()].toUpperCase() + ' ' + installmentDate.getFullYear()
    const date = dayjs(dateValue).format('DD/MM/YYYY')
    whatsAppText = lastInstallment
      ? whatsAppText.concat(
          `${title(lastInstallmentInfo)}\n`,
          `${lastInstallmentInfo?.conditionDescription || ''} ${originalDate(lastInstallmentDate)}\n`,
          `${date}\n`,
          `${totalValue(lastInstallmentInfo)}`
        )
      : whatsAppText
    whatsAppText =
      lastInstallment && penultimateInstallment
        ? whatsAppText.concat(formatMessage(messages.financeFutureScreen_whatsApp_message_and))
        : whatsAppText
    whatsAppText = penultimateInstallment
      ? whatsAppText.concat(
          `${title(penultimateInstallmentInfo)}\n`,
          `${penultimateInstallmentInfo?.conditionDescription || ''} ${originalDate(penultimateInstallmentDate)}\n`,
          `${date}\n`,
          `${totalValue(penultimateInstallmentInfo)}`
        )
      : whatsAppText
    openWhatsApp(whatsAppText, BRIO_SAC_WHATSAPP_NUMBER)
  }

  const onSelectDropDown = item => {
    setSaleContractId(userSaleContracts[item?.id]?.id)
    setValue(item.text)
  }

  const onChangePartialValue = (text: string) => {
    if (getOnlyNumbers(text) === '00') {
      setAdditionalValue('')
    } else {
      setAdditionalValue(`R$${currencyMask(text)}`)
    }
  }

  return (
    <ScreenContainer>
      <ATLDropdownSelector
        value={value}
        horizontalMargin={atlTheme.SpacingMarginal}
        dropdownData={dropdownUserSaleContractsData}
        title={formatMessage(messages.financeFutureScreen_dropdown_title)}
        leftIcon={icons.towerIcon}
        textInputBaseProps={{ leftIconColor: atlTheme.ColorBrand2Default }}
        onChange={onSelectDropDown}
      />
      <ATLTextBody
        labelBaseProps={{
          multilineLimit: 1,
          fontSize: atlTheme.FontSizeHeading,
          lineHeight: atlTheme.LineHeightHeading,
          fontWeight: FontWeightEnum.medium,
        }}
      >
        {formatMessage(messages.financeFutureScreen_selectInstallment)}
      </ATLTextBody>
      <ATLCheckbox
        horizontalMargin={atlTheme.SpacingMarginal}
        texts={[
          {
            text: `${lastInstallmentInfo?.conditionDescription || ''} ${shortMonth[
              lastInstallmentDate.getMonth()
            ].toUpperCase()} ${lastInstallmentDate.getFullYear()} • ${ifMoneyInt(lastInstallmentInfo.currentBalance)}`,
          },
        ]}
        value={lastInstallment}
        onChange={() => setLastInstallment(!lastInstallment)}
        buttonToggleBaseProps={{ tintColor: atlTheme.ColorBrand2Default }}
      />
      <ATLCheckbox
        horizontalMargin={atlTheme.SpacingMarginal}
        texts={[
          {
            text: `${lastInstallmentInfo?.conditionDescription || ''} ${shortMonth[
              penultimateInstallmentDate.getMonth()
            ].toUpperCase()} ${penultimateInstallmentDate.getFullYear()} • ${ifMoneyInt(
              penultimateInstallmentInfo.currentBalance
            )}`,
          },
        ]}
        value={penultimateInstallment}
        onChange={() => setPenultimateInstallment(!penultimateInstallment)}
        buttonToggleBaseProps={{ tintColor: atlTheme.ColorBrand2Default }}
      />
      <ATLDateTimePicker
        title={formatMessage(messages.financeFutureScreen_paymentDate)}
        leftIcon={icons.date}
        textInputBaseProps={{ leftIconColor: atlTheme.ColorBrand2Default, labelColor: atlTheme.ColorMediumContrast }}
        horizontalMargin={atlTheme.SpacingMarginal}
        value={dateValue}
        onChange={value => {
          setDateValue(value)
        }}
        minDate={today}
      />
      <ATLSwitchButton
        switchValue={payPartialValue}
        onValueChangeSwitch={() => setPayPartialValue(!payPartialValue)}
        text={formatMessage(messages.financeFutureScreen_payPartialValue)}
        switchBaseProps={{ activeColor: atlTheme.ColorBrand2Default }}
      />
      {payPartialValue && (
        <ATLTextInput
          hideClearButton
          leftIcon={icons.moneyIcon}
          horizontalMargin={atlTheme.SpacingMarginal}
          keyboardType={platformConfig.isWeb() ? undefined : KeyboardTypeEnum.numberPad}
          title={formatMessage(messages.financeFutureScreen_addValue)}
          textInputBaseProps={{ focusColor: atlTheme.ColorMediumContrast }}
          value={additionalValue}
          onChange={onChangePartialValue}
        />
      )}
      <ButtonContainer>
        <ATLButton
          title={formatMessage(messages.financeFutureScreen_sendToSAC)}
          onPress={onPressSAC}
          buttonBaseProps={{ borderRadius: atlTheme.BorderRadiusFloatingButton, fontWeight: FontWeightEnum.medium }}
          type={ButtonTypeEnum.outlined}
          horizontalMargin={0.1}
          verticalMargin={atlTheme.SpacingMarginal}
          disabled={
            !dateValue || (!lastInstallment && !penultimateInstallment) || (payPartialValue && !additionalValue)
          }
        />
      </ButtonContainer>
    </ScreenContainer>
  )
}
AnticipateInstallmentScreen.navigationOptions = CustomHeader(
  formatMessage(messages.financeFutureScreen_anticipateInstallment_header),
  true,
  () => Coordinator.goBack()
)
